import { EmailData } from './DataResolver';

type StringifyNoNull<T> = T extends any[]
  ? string
  : T extends object
  ? {
      [K in keyof T]-?: K extends 'custom'
        ? Record<string, string>
        : StringifyNoNull<Exclude<T[K], null>>;
    }
  : string;

type ExtendedEmailData = EmailData & {
  publisher_name?: string;
  role?: string;
  sign_up_url_link?: string;
  sign_up_url_link_email?: string;
  sign_up_url_mail_link?: string;
  reset_password_link?: string;
  reset_password_url_mail_link?: string;
  temporary_password?: string;
  user_updated_fields?: string;
  login_url_link?: string;
  co_author_name?: string;
  signup_progress_mail?: string;
  signup_progress_url_mail_link?: string;
  app_name?: string;
  forgot_password_link?: string;
  forgot_password_link_email?: string;
  time_period?: string;
  merge_details?: string;
  update_primary_email_verification_link?: string;
};

type EmailDataNoNullStrings = StringifyNoNull<ExtendedEmailData>;

const hostUrlTemplateValue =
  (
    globalThis as typeof globalThis & {
      process?: { env?: { HOST_URL?: string } };
    }
  ).process?.env?.HOST_URL ?? 'HOST_URL';

export const templateVariables: EmailDataNoNullStrings = {
  role: 'Resolved role for the target user in the event context',
  reviewer: {
    id: "Reviewer's ID",
    email: "Reviewer's email address",
    salutation: "Reviewer's salutation",
    first_name: "Reviewer's first name",
    last_name: "Reviewer's last name",
    full_name: "Reviewer's full name (salutation first name last name)",
    orcid: "Reviewer's ORCID"
  },
  review: {
    id: 'Review ID',
    reviewer_email: "Reviewer's email address",
    co_reviewer_email: "Co-reviewer's email address",
    co_reviewer_invitation_note: 'Invitation note for co-reviewer',
    due_date: 'Deadline for the current task on the review',
    reviewer_url: 'URL of the review accessible to the reviewer'
  },
  submission: {
    id: 'Human readable ID of the current manuscript',
    manuscript_id: 'Human readable ID of the manuscript',
    submission_id: 'Persistent ID of the manuscript',
    title: 'Title of the current manuscript',
    article_type: 'Document type',
    abstract: 'Abstract of the current manuscript',
    due_date: "Date the reviewer's scoresheet is due",
    reviewer_comments: 'Reviewers comments to the author',
    decision_comments: "Editor's comments on the decision",
    decision:
      'Decision that has been made on the current version of the manuscript',
    author_url: 'Submission URL accessible to the author',
    editor_url: 'Submission URL accessible to the editor',
    revision_number: 'Submission revision number',
    comments_to_editor:
      'All reviewers comments to the editor as entered in their scoresheets',
    comments_from_author: 'Comments from the author in revision',
    author_names: 'All author names of the manuscript.',
    author_emails: 'All author emails of the manuscript.',
    co_authors_emails: 'All co-author emails of the manuscript.',
    previous_comments_to_author:
      'Comments to author by reviewer in previous version of revision.'
  },
  reviews: {
    first_assess_for_author:
      'Moderated feedback from reviewers for authors from the first assessment',
    rev_assess_for_author:
      'Moderated feedback from reviewers for authors from revision assessment'
  },
  organization: {
    id: 'Organization ID',
    name: 'Organization name',
    shortname: 'Organization short name',
    idp: 'Organization identity provider'
  },
  workspace: {
    id: 'Workspace ID',
    name: 'Workspace name',
    shortname: 'Workspace short name'
  },
  journal: {
    id: 'Journal ID',
    name: 'Full name of the journal',
    shortname: 'Acronym of the journal',
    introduction: 'The journal blurb',
    is_coreview_enabled: 'Whether co-review is enabled for the journal',
    description: 'The journal description as entered in journal setup'
  },
  submitting_author: {
    id: "Submitting author's ID",
    email: "Submitting author's email address",
    salutation: "Submitting author's salutation",
    first_name: "Submitting author's first name",
    last_name: "Submitting author's last name",
    full_name: "Submitting author's full name",
    orcid: "Submitting author's ORCID iD"
  },
  adjudications: {
    feedback_for_authors: 'All comments for authors from adjudicators',
    recommendation: 'All recommendations from adjudicators'
  },
  corresponding_author: {
    id: "Corresponding author's ID",
    email: "Corresponding author's email address",
    salutation: "Corresponding author's salutation",
    first_name: "Corresponding author's first name",
    last_name: "Corresponding author's last name",
    full_name: "Corresponding author's full name",
    orcid: "Corresponding author's ORCID iD"
  },
  // recipient of the email
  person: {
    id: "Recipient person's unique identifier",
    email: "Recipient person's email address",
    salutation: "Recipient person's salutation",
    first_name: "Recipient person's first name",
    last_name: "Recipient person's last name",
    full_name: "Recipient person's full name (salutation first name last name)",
    orcid: "Recipient person's ORCID iD"
  },
  email_list: {
    all_journal_editors: "List of all journal editors' email addresses",
    all_submission_reviewers:
      "List of all submission reviewers' email addresses",
    submitted_reviewers:
      'List of email addresses of reviewers who have submitted a review',
    invited_in_progress_reviewers:
      'List of email addresses of reviewers who are invited or in-progress (not yet submitted)',
    current_review_round_active_reviewers:
      'List of email addresses of active reviewers in the current peer review round (invited, in progress, or completed)',
    all_board_members: "List of all board members' email addresses",
    all_submission_editors_auto_assign:
      "List of submission auto-assign editors' email addresses",
    reviewers_invited_by_editors:
      'Email addresses of the editors who invited the reviewers whose completed reports met the target'
  },
  queue: {
    emails: 'List of all reviewer email addresses from the review queue.'
  },
  // sender
  user: {
    id: "Sending user's ID",
    email: "Sending user's e-mail address",
    first_name: "Sending user's first name",
    last_name: "Sending user's last name",
    full_name: "Sending user's full name (salutation first name last name)",
    orcid: "Sending user's ORCID iD",
    salutation: "Sending user's salutation"
  },
  adjudication: {
    due_date: 'Deadline for submitting the adjudication',
    expert_url: 'URL of the adjudication form'
  },
  early_assessment: {
    due_date: 'Deadline for submitting the early assessment',
    expert_url: 'URL of the early assessment form'
  },
  html: {
    review_invite_with_coreview: 'Invitation buttons to accept or decline',
    review_invite_buttons:
      'The invitation responses that are configured to send with the email',
    accept_review_url: 'Raw URL to accept the review invitation',
    decline_review_url: 'Raw URL to decline the review invitation',
    review_link: 'A link to the review form',
    site_url: `URL of the site (e.g., ${hostUrlTemplateValue})`,
    workspace_invite_link: 'Link to invite to the workspace',
    accept_co_review_invitation: 'HTML for accept co-review invitation button',
    decline_co_review_invitation:
      'HTML for decline co-review invitation button',
    download_export_button: 'Download button for exported submission data',
    view_submission: 'View submission button',
    download_author_copyright_button: 'Download author copyright agreement',
    download_author_icmje_button: 'Download author ICMJE agreement',
    reset_password_link: 'Reviewer password reset link',
    contact_publisher_subject: 'Email subject for contacting publisher',
    contact_publisher_message: 'Email body for contacting publisher'
  },
  generated: {
    date: 'Current date'
  },
  note: {
    tagged_journal_name: 'Full name of the journal tagged in the note',
    tagged_journal_email: 'Email of the journal tagged in the note',
    tagged_users: 'List of the users tagged in the note',
    content_truncated: 'Content of the note, shortened',
    url: 'URL of the note'
  },
  transfer: {
    referred_journal_description: 'Journal description',
    sending_journal: 'Sending journal name',
    target_journal: 'Target journal name',
    transfer_choices_author: 'Transfer choices to author',
    transferred_comments_to_author: 'Transfer comments to author'
  },

  // for org-admin defined variables
  custom: {},
  publisher_name: 'Publisher name',
  sign_up_url_link: 'Sign-up form link',
  sign_up_url_link_email: 'Sign-up form link with email',
  sign_up_url_mail_link: 'Raw sign-up form link',
  reset_password_link: 'Reset password link',
  reset_password_url_mail_link: 'Raw reset password link',
  temporary_password: 'User login temporary password',
  user_updated_fields: 'User updated details',
  login_url_link: 'User login page link',
  co_author_name: 'Co-author name',
  signup_progress_mail: 'Signup onboarding progress',
  signup_progress_url_mail_link: 'Raw signup onboarding progress link',
  app_name: 'App name',
  forgot_password_link: 'Forgot password link',
  forgot_password_link_email: 'Forgot password link with email',
  time_period: 'Link validity period derived from the template variable in use',
  customer_support_email: 'Email address for all user support queries',
  update_primary_email_verification_link:
    'Link for the user to confirm a new primary email address',
  merge_details:
    'Summary of merged accounts, moved submissions/drafts/reviews, and merge date',
  inbound_email_content: 'Inbound email content',
  previous_manuscript_id:
    'Previous manuscript ID from which submission was cloned',

  submission_owner: {
    id: "Submission owner user's ID",
    email: "Submission owner user's e-mail address",
    first_name: "Submission owner user's first name",
    last_name: "Submission owner user's last name",
    full_name:
      "Submission owner user's full name (salutation first name last name)",
    orcid: "Submission owner user's ORCID iD",
    salutation: "Submission owner user's salutation"
  },

  assignee: {
    id: "Editor assignee's ID",
    email: "Editor assignee's email address",
    first_name: "Editor assignee's first name",
    last_name: "Editor assignee's last name",
    full_name: "Editor assignee's full name (salutation first name last name)",
    orcid: "Editor assignee's ORCID iD",
    salutation: "Editor assignee's salutation",
    role: 'Editor role of the assignee'
  }
};

type VarDesc = { var: string; desc: string };

function flattenTemplateVariables(
  tpl: Record<string, any>,
  prefix = ''
): VarDesc[] {
  return Object.entries(tpl).flatMap(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'string') {
      return { var: path, desc: val };
    }
    if (val && typeof val === 'object') {
      return flattenTemplateVariables(val, path);
    }
    return [];
  });
}

// Strip braces from a string
function stripBraces(str: string): string {
  return str.replace(/[{}]/g, '');
}

// Search first in var then desc, stripping { } before matching
export function searchVars(arr: VarDesc[], term: string): VarDesc[] {
  const t = term.toLowerCase();

  const varMatches = arr.filter(vd =>
    stripBraces(vd.var).toLowerCase().includes(t)
  );

  const descMatches = arr
    .filter(vd => !stripBraces(vd.var).toLowerCase().includes(t))
    .filter(vd => stripBraces(vd.desc).toLowerCase().includes(t));

  return [...varMatches, ...descMatches];
}

export function toVarDescArray(tpl: Record<string, any>): VarDesc[] {
  return flattenTemplateVariables(tpl).sort((a, b) =>
    a.var.localeCompare(b.var)
  );
}

export const existingVariables = new Set(
  flattenTemplateVariables(templateVariables).map(v => v.var)
);
